// The install page at "/install". Offers the PWA install prompt where the
// browser supports it (Chrome/Edge/Android), Add-to-Home-Screen instructions
// on iOS/Safari, and a link into the app for anyone who just wants to use it
// in-browser. Standalone (already-installed) launches never land here — the
// shell routes them straight into the app.

import { canInstall, onInstallabilityChange, promptInstall } from '../install-prompt.js';

interface InstallOpts {
  /** Open the app (login) in the current browser instead of installing. */
  onOpenInApp: () => void;
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
    // iPadOS 13+ reports as MacIntel with a touch screen.
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function installScreen(opts: InstallOpts): HTMLElement {
  const root = document.createElement('div');
  root.style.cssText = `
    min-height: 100vh; box-sizing: border-box;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    text-align: center; padding: 2rem;
    background: #0f1923; color: #e0e0e0;
  `;

  const card = document.createElement('div');
  card.style.cssText = 'width: 100%; max-width: 360px;';

  const icon = document.createElement('span');
  icon.textContent = '$';
  icon.style.cssText = `
    font-size: 3rem; color: #2d8a5e; font-weight: 700;
    font-family: 'SF Mono', 'Fira Code', monospace;
    display: block; line-height: 1; margin-bottom: 1rem;
  `;

  const title = document.createElement('h1');
  title.textContent = 'Install CoinHealth';
  title.style.cssText = 'font-size: 1.5rem; font-weight: 700; margin: 0 0 0.5rem;';

  const blurb = document.createElement('p');
  blurb.textContent = 'Add CoinHealth to your home screen for a full-screen, app-like experience.';
  blurb.style.cssText = 'color: #8899aa; font-size: 0.9rem; line-height: 1.5; margin: 0 0 1.5rem;';

  card.append(icon, title, blurb);

  // Native install button (Chrome/Edge/Android). Hidden until the browser has
  // handed us a prompt to trigger.
  const installBtn = document.createElement('button');
  installBtn.textContent = 'Install CoinHealth';
  installBtn.style.cssText = `
    width: 100%; background: #2d8a5e; color: #fff; border: none;
    border-radius: 8px; padding: 0.85rem; font-size: 1rem; font-weight: 600;
    cursor: pointer; font-family: inherit; display: none;
  `;
  installBtn.addEventListener('click', async () => {
    installBtn.disabled = true;
    const outcome = await promptInstall();
    installBtn.disabled = false;
    if (outcome === 'accepted') {
      installBtn.textContent = 'Installed';
      installBtn.style.display = 'none';
    }
  });

  // iOS / fallback instructions. Shown when there is no native prompt.
  const instructions = document.createElement('div');
  instructions.style.cssText = 'color: #6b7f8e; font-size: 0.85rem; line-height: 1.6;';
  if (isIOS()) {
    instructions.innerHTML =
      'Tap the <strong>Share</strong> button, then <strong>Add to Home Screen</strong>.';
  } else {
    instructions.innerHTML =
      'Open your browser menu and choose <strong>Install</strong> or <strong>Add to Home Screen</strong>.';
  }

  card.append(installBtn, instructions);

  // Reflect install availability: show the native button, hide the manual
  // instructions when a prompt is (or becomes) available.
  function syncInstallability(): void {
    const available = canInstall();
    installBtn.style.display = available ? 'block' : 'none';
    instructions.style.display = available ? 'none' : 'block';
  }
  syncInstallability();
  onInstallabilityChange(syncInstallability);

  // Path into the app for browser-only users.
  const openLink = document.createElement('button');
  openLink.textContent = 'Or continue in your browser';
  openLink.style.cssText = `
    background: none; border: none; color: #2d8a5e; cursor: pointer;
    font-size: 0.85rem; font-family: inherit; margin-top: 1.5rem;
    display: block; width: 100%; text-align: center;
  `;
  openLink.addEventListener('click', opts.onOpenInApp);
  card.append(openLink);

  root.append(card);
  return root;
}
